import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";

// The top-bar account menu. The trigger is labeled with the broker's brokerage
// name; the dropdown holds the broker's own destinations (Profile, Account
// security) plus Sign out. Sign-out lives here — the shell owns it now, not the
// portal page. Accessible: aria-haspopup + aria-expanded on the trigger; the
// panel closes on Escape, an outside click, and on selecting an item.
interface BrokerageMenuProps {
  /** Brokerage name shown on the trigger (a neutral fallback while it loads). */
  brokerageName: string;
}

export function BrokerageMenu({ brokerageName }: BrokerageMenuProps) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on Escape or a click outside the menu, but only while it's open.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  async function onSignOut() {
    setSigningOut(true);
    await signOut();
    navigate("/", { replace: true });
  }

  const itemClass =
    "block w-full px-4 py-2 text-left text-sm text-brk-text transition-colors hover:bg-brk-bg";

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className="flex max-w-[10rem] items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-brk-text shadow-sm transition-colors hover:bg-brk-bg sm:max-w-xs"
      >
        <span className="truncate">{brokerageName}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path
            d="m6 9 6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-1 w-56 overflow-hidden rounded-md border border-brk-border bg-brk-panel py-1 shadow-lg">
          <Link
            to="/profile"
            onClick={() => setOpen(false)}
            className={itemClass}
          >
            Profile
          </Link>
          <Link
            to="/security"
            onClick={() => setOpen(false)}
            className={itemClass}
          >
            Account security
          </Link>
          <Link
            to="/preferences"
            onClick={() => setOpen(false)}
            className={itemClass}
          >
            Notification preferences
          </Link>
          <button
            type="button"
            onClick={onSignOut}
            disabled={signingOut}
            className={`${itemClass} text-brk-danger disabled:opacity-60`}
          >
            {signingOut ? "Signing out…" : "Sign out"}
          </button>
        </div>
      )}
    </div>
  );
}
